import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Route, Switch} from 'react-router-dom';
import {ConnectedRouter} from 'connected-react-router';
import {createStructuredSelector} from 'reselect';
import {history} from 'config/store';
import {authenticated, anonymous} from 'config/routes';
import {getToken} from 'api/auth';
import {
  apiNotifications,
  getNotificationsEnabledSelector,
  setNotificationsEnabled,
} from 'api/notifications';
import {
  waitForOneSignal,
  isPushOptedIn,
  onPushSubscriptionChange,
  onNotificationClick,
} from 'lib/onesignal';
import {toAppRoute} from 'lib/siteLinks';
import Tabs from 'ui/Tabs';
import AssistantSwitch from 'ui/AssistantSwitch';
import ScrollToTop from 'ui/ScrollToTop';
import SiteLinkInterceptor from 'ui/SiteLinkInterceptor';
import {NotificationContainer} from 'ui/toastify/styles';
// import {ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {lifecycle} from 'recompose';
import PushNotificationPrompt from 'ui/PushNotificationPrompt';
import smoothscroll from 'smoothscroll-polyfill';
import {isBrowserAccessAllowed, isChatbotAiEnabled} from 'lib/urlFeatureFlags';
import {isPhone, isStandalone} from 'lib/displayMode';
import DesktopNotice from 'ui/DesktopNotice';
import InstallGate from 'ui/InstallGate';

// make scrollIntoView smooth on safari
smoothscroll.polyfill();
const chatbotAiEnabled = isChatbotAiEnabled(window.location.search);
// Sur ordinateur et tablette, l'application n'est pas affichée.
const phone = isPhone();
// Sur téléphone, l'application ne s'utilise qu'installée : dans le navigateur, seul l'écran
// d'installation est affiché (sauf porte de service « ?navigateur=1 »).
const installRequired = phone && !isStandalone() && !isBrowserAccessAllowed(window.location.search);

const Root = ({token}) =>
  !phone ? (
    <DesktopNotice />
  ) : installRequired ? (
    <InstallGate />
  ) : (
    <ConnectedRouter history={history}>
      <ScrollToTop />
      <SiteLinkInterceptor />
      <Switch>
        {(token ? authenticated : anonymous).map(({path, Component, exact = true}) => (
          <Route key={path} exact={exact} path={path}>
            <Component />
          </Route>
        ))}
      </Switch>
      {token && <Tabs />}
      {token && chatbotAiEnabled && <AssistantSwitch token={token} />}
      {token && <PushNotificationPrompt />}
      <NotificationContainer />
    </ConnectedRouter>
  );

Root.propTypes = {
  token: PropTypes.string,
};

export default Root
  |> lifecycle({
    componentDidMount() {
      setTimeout(() => {
        const e = document.querySelector('#splashscreen');
        if (e) {
          e.remove();
        }
      }, 1900);
      setInterval(() => {
        if (this.props.token && this.props.notificationsEnabled) {
          this.props.getNotification();
        }
      }, 10_000);

      waitForOneSignal()
        .then(() => {
          this.props.setNotificationsEnabled(isPushOptedIn());
          this.unsubscribePush = onPushSubscriptionChange(optedIn => {
            this.props.setNotificationsEnabled(optedIn);
          });
          // Appli déjà ouverte : OneSignal la met au premier plan, l'appli ouvre l'écran visé.
          this.unsubscribeClick = onNotificationClick(url => {
            const route = toAppRoute(url);
            if (route && route !== history.location.pathname) history.push(route);
          });
        })
        .catch(e => console.warn('OneSignal init failed', e));
    },
    componentWillUnmount() {
      if (this.unsubscribePush) this.unsubscribePush();
      if (this.unsubscribeClick) this.unsubscribeClick();
    },
  })
  |> connect(
    createStructuredSelector({token: getToken, notificationsEnabled: getNotificationsEnabledSelector}),
    {
      getNotification: apiNotifications.getNotification,
      setNotificationsEnabled,
    },
  );
